import type { Logger } from '@kinopoisk-int/logger';

import {
    IYandexMetrikaInitParams,
    IYandexMetrikaCounterParams,
    IYandexMetrikaEvents,
    IYandexMetrikaApi,
    IYandexMetrika,
    YandexMetrikaMethods,
    IYandexMetrikaConstructor,
    GlobalCounter,
} from './types';

const ASYNC_LOAD_CALLBACK_NAME = 'yandex_metrika_callbacks2';
const SCRIPT_SRC = 'https://mc.yandex.ru/metrika/tag.js';
const DEFAULT_MAX_TRIES = 1;
const DEFAULT_TIMEOUT = 2 * 1000; // 2 secs

const mapYandexMetrikaInitialParamsToCounterParams = (
    initialParams: IYandexMetrikaInitParams
): IYandexMetrikaCounterParams => ({
    id: initialParams.id,
    clickmap: initialParams.clickmap,
    trackLinks: initialParams.trackLinks,
    accurateTrackBounce: initialParams.accurateTrackBounce,
    webvisor: initialParams.webvisor,
    experiments: initialParams.experiments,
    params: initialParams.params,
    yaGDPRLang: initialParams.yaGdprLang,
});

export class YandexMetrikaApi implements IYandexMetrikaApi {
    private metrika: IYandexMetrika | null = null;

    private asyncEventsBuffer: IYandexMetrikaEvents[] = [];

    private params: IYandexMetrikaCounterParams;

    private scriptSrc: string;

    private asyncLoadCallbackName: string;

    private resolveYmclidPromise: (ymclid?: string) => void = () => {};

    private initialParams: IYandexMetrikaInitParams;

    private maxTries: number;

    private timeout: number;

    private triesCounter: number = 0;

    private getLogger?: () => Logger;

    private onError?: () => void;

    private onSuccess?: () => void;

    ymclidPromise: Promise<string | undefined>;

    constructor({
        initialParams,
        scriptSrc = SCRIPT_SRC,
        asyncLoadCallbackName = ASYNC_LOAD_CALLBACK_NAME,
        maxTries = DEFAULT_MAX_TRIES,
        timeout = DEFAULT_TIMEOUT,
        getLogger,
        onError,
        onSuccess,
    }: {
        initialParams: IYandexMetrikaInitParams;
        scriptSrc?: string;
        asyncLoadCallbackName?: string;
        maxTries?: number;
        getLogger?: () => Logger;
        timeout?: number;
        onError?: () => void;
        onSuccess?: () => void;
    }) {
        this.initialParams = initialParams;
        this.scriptSrc = scriptSrc;
        this.asyncLoadCallbackName = asyncLoadCallbackName;
        this.params = mapYandexMetrikaInitialParamsToCounterParams(initialParams);
        this.ymclidPromise = new Promise((resolve) => {
            this.resolveYmclidPromise = resolve;
        });
        this.maxTries = maxTries;
        this.getLogger = getLogger;
        this.timeout = timeout;
        this.onError = onError;
        this.onSuccess = onSuccess;

        this.init(initialParams.isMetrikaInitParamsLoggingEnabled);
    }

    private async init(isMetrikaInitParamsLoggingEnabled: boolean = false) {
        const Metrika = await this.load();

        if (!Metrika) {
            this.onError?.();
            return;
        }

        this.onSuccess?.();

        const metrikaParams = this.params;

        this.metrika = new Metrika(metrikaParams);

        if (isMetrikaInitParamsLoggingEnabled) {
            this.metrika?.getClientID?.((clientId) => {
                this.getLogger?.().info(
                    {
                        metrika: {
                            ...metrikaParams,
                            clientId,
                        },
                    },
                    `Metrika counter with id ${metrikaParams.id} initialized`
                );
            });
        }

        this.metrika?.getYmclid?.(this.resolveYmclidPromise);
        this.initGlobalCounter(this.metrika);

        this.asyncEventsBuffer.forEach((event) => {
            this.handleMetrikaEvent(event);
        });

        this.asyncEventsBuffer = [];
    }

    private getMetrikaSdkApi = (): IYandexMetrikaConstructor | undefined => window.Ya?.Metrika2;

    private async doLoad() {
        const loadedMetrikaApi = this.getMetrikaSdkApi();

        if (loadedMetrikaApi) {
            return loadedMetrikaApi;
        }

        return new Promise<IYandexMetrikaConstructor>((resolve, reject) => {
            const handleError = () => {
                metrikaScriptElement?.remove();
                clearTimeout(timer);
                reject();
            };

            const timer = setTimeout(handleError, this.timeout);

            let metrikaScriptElement = document.querySelector<HTMLScriptElement>(
                `script[src*="${this.scriptSrc}"]`
            );
            if (!metrikaScriptElement) {
                metrikaScriptElement = document.createElement('script');

                metrikaScriptElement.type = 'text/javascript';
                metrikaScriptElement.async = true;
                metrikaScriptElement.src = this.scriptSrc;
                metrikaScriptElement.onerror = handleError;

                document.body.append(metrikaScriptElement);
            }

            let asyncLoadCallback = window[this.asyncLoadCallbackName];

            if (!Array.isArray(asyncLoadCallback)) {
                asyncLoadCallback = [];

                window[this.asyncLoadCallbackName] = asyncLoadCallback;
            }

            asyncLoadCallback.push(() => {
                const metrikaApi = this.getMetrikaSdkApi();

                if (metrikaApi) {
                    clearTimeout(timer);
                    resolve(metrikaApi);
                } else {
                    handleError();
                }
            });
        });
    }

    private async load() {
        for (; this.triesCounter < this.maxTries; this.triesCounter++) {
            try {
                // eslint-disable-next-line no-await-in-loop
                return await this.doLoad();
            } catch (error) {
                this.getLogger?.().warn(
                    { error, counterId: this.initialParams.id, triesCounter: this.triesCounter },
                    'Loading attempt of metrika tag has been failed'
                );
            }
        }

        // invariant: this.triesCounter >= this.maxTries
        this.getLogger?.().error(
            { counterId: this.initialParams.id, triesCounter: this.triesCounter },
            "Metrika tag hasn't been loaded"
        );

        return undefined;
    }

    private initGlobalCounter(metrika: IYandexMetrika) {
        const counterKey: GlobalCounter = `yaCounter${this.params.id}`;
        window[counterKey] = metrika;
    }

    private handleMetrikaEvent(event: IYandexMetrikaEvents) {
        if (!this.metrika) {
            return;
        }

        switch (event.type) {
            case YandexMetrikaMethods.params:
                this.metrika.params(...event.payload);
                break;

            case YandexMetrikaMethods.reachGoal:
                this.metrika.reachGoal(...event.payload);
                break;

            case YandexMetrikaMethods.hit: {
                this.metrika.hit(...event.payload);
                break;
            }
            default: {
                break;
            }
        }
    }

    emit(event: IYandexMetrikaEvents) {
        if (this.metrika) {
            this.handleMetrikaEvent(event);
        } else {
            this.asyncEventsBuffer.push(event);
        }
    }
}
